import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Progress } from '../ui/progress';
import {
  Upload, FileText, FileX, Folder, Archive, Repeat, ListChecks,
  Loader2, CheckCircle2, AlertCircle, Download, ChevronDown, ChevronRight, X,
} from 'lucide-react';
import {
  CHUNK_SIZE, SUPPORTED_MIMES, formatBytes, evaluateFile, walkEntry,
} from './libraryHelpers';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatTime = (s) => {
  if (s == null) return '—';
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m ${s % 60}s`;
  return `${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}m`;
};

const Uploader = ({ onComplete, resumeFilter, setResumeFilter }) => {
  const [draggingOver, setDraggingOver] = useState(false);
  const [batch, setBatch] = useState(null); // {discovered, skipped[], queue[], inFlight[], completed[], failed[], startedAt}
  const [showDetails, setShowDetails] = useState(false);
  const fileInputRef = useRef(null);
  const folderInputRef = useRef(null);
  const zipInputRef = useRef(null);
  const reportInputRef = useRef(null);

  const inFlightRef = useRef(0);
  const concurrencyRef = useRef(2); // fixed at 2 — safe for production proxies
  const successStreakRef = useRef(0);
  const queueRef = useRef([]); // source of truth for the queue (synchronous pop)
  const completedRef = useRef([]);
  const failedRef = useRef([]);
  const inFlightPathsRef = useRef(new Set());
  const tickHandleRef = useRef(null);

  // Beforeunload guard while uploading
  useEffect(() => {
    const active = batch && batch.queue.length + batch.inFlight.length > 0;
    if (!active) return;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = 'Library upload in progress — leaving will cancel pending files.';
      return e.returnValue;
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [batch]);

  // Retry a callable on transient errors (network errors / 5xx / 429).
  // Exponential backoff: 1s, 3s, 7s.
  const withRetry = useCallback(async (fn, label, maxAttempts = 4) => {
    let lastErr;
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        // eslint-disable-next-line no-await-in-loop
        return await fn();
      } catch (err) {
        lastErr = err;
        const status = err?.response?.status;
        const isRetryable = !err?.response || status === 429 || (status >= 500 && status < 600);
        if (!isRetryable || attempt === maxAttempts) {
          throw err;
        }
        const backoffMs = Math.min(7000, 1000 * Math.pow(2, attempt - 1) + Math.random() * 500);
        // eslint-disable-next-line no-await-in-loop
        await new Promise((resolve) => setTimeout(resolve, backoffMs));
      }
    }
    throw lastErr;
  }, []);

  const uploadOneFile = useCallback(async ({ file, folderTags, relativePath }) => {
    const initRes = await withRetry(
      () => axios.post(`${API}/library/init-upload`, {
        filename: file.name,
        total_size: file.size,
      }, { timeout: 30000 }),
      `init:${relativePath}`,
    );
    const uploadId = initRes.data.upload_id;
    let sent = 0;
    while (sent < file.size) {
      const end = Math.min(sent + CHUNK_SIZE, file.size);
      const chunk = file.slice(sent, end);
      // eslint-disable-next-line no-await-in-loop
      await withRetry(
        () => axios.post(`${API}/library/upload-chunk`, chunk, {
          headers: { 'X-Upload-Id': uploadId, 'Content-Type': 'application/octet-stream' },
          timeout: 90000,
        }),
        `chunk:${relativePath}@${sent}`,
      );
      sent = end;
    }
    const tags = (folderTags || []).filter(Boolean).map((t) => t.toLowerCase().slice(0, 64));
    await withRetry(
      () => axios.post(`${API}/library/finalize`, {
        upload_id: uploadId,
        filename: file.name,
        tags,
      }, { timeout: 30000 }),
      `finalize:${relativePath}`,
    );
    return { relativePath, size: file.size };
  }, [withRetry]);

  // Manage upload via a ref-based scheduler. Queue state lives in queueRef so
  // multiple workers can pop synchronously without React's setState async race.
  const syncBatchStateFromRefs = useCallback(() => {
    setBatch((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        queue: [...queueRef.current],
        inFlight: [...inFlightPathsRef.current],
        completed: [...completedRef.current],
        failed: [...failedRef.current],
      };
    });
  }, []);

  const tryLaunchWorkers = useCallback(async () => {
    while (
      inFlightRef.current < concurrencyRef.current &&
      queueRef.current.length > 0
    ) {
      const next = queueRef.current.shift();
      inFlightRef.current += 1;
      inFlightPathsRef.current.add(next.relativePath);
      syncBatchStateFromRefs();
      (async () => {
        try {
          await uploadOneFile(next);
          completedRef.current.push({ path: next.relativePath, size: next.file.size });
        } catch (err) {
          const reason = err.response?.data?.detail || err.message || 'Upload failed';
          failedRef.current.push({ path: next.relativePath, reason, size: next.file.size });
        } finally {
          inFlightRef.current -= 1;
          inFlightPathsRef.current.delete(next.relativePath);
          syncBatchStateFromRefs();
          if (tickHandleRef.current == null) {
            tickHandleRef.current = setTimeout(() => {
              tickHandleRef.current = null;
              tryLaunchWorkers();
            }, 0);
          }
        }
      })();
    }
    if (queueRef.current.length === 0 && inFlightRef.current === 0) {
      setTimeout(() => {
        setBatch((prev) => {
          if (!prev) return prev;
          if (prev.queue.length === 0 && prev.inFlight.length === 0) {
            const totalUploaded = prev.completed.length;
            const totalFailed = prev.failed.length;
            const totalSkipped = prev.skipped.length;
            if (totalUploaded > 0) toast.success(`Uploaded ${totalUploaded} file${totalUploaded !== 1 ? 's' : ''}`);
            if (totalFailed > 0) toast.error(`${totalFailed} file${totalFailed !== 1 ? 's' : ''} failed`);
            if (totalSkipped > 0) toast.info(`${totalSkipped} file${totalSkipped !== 1 ? 's' : ''} skipped`);
            onComplete?.();
          }
          return prev;
        });
      }, 100);
    }
  }, [uploadOneFile, syncBatchStateFromRefs, onComplete]);

  // Server-side dedup check
  const checkExistingOnServer = useCallback(async (items) => {
    const existing = new Set();
    const BATCH = 500;
    for (let i = 0; i < items.length; i += BATCH) {
      const slice = items.slice(i, i + BATCH).map((it) => ({
        filename: it.file.name,
        size: it.file.size,
      }));
      try {
        // eslint-disable-next-line no-await-in-loop
        const res = await withRetry(
          () => axios.post(`${API}/library/check-exists`, { items: slice }, { timeout: 30000 }),
          `check-exists:batch-${i}`,
          3,
        );
        (res.data.existing_keys || []).forEach((k) => existing.add(k));
      } catch (err) {
        console.warn('check-exists failed for batch', i, err?.message);
      }
    }
    return existing;
  }, [withRetry]);

  const startBatch = useCallback(async (items, skipped) => {
    completedRef.current = [];
    failedRef.current = [];
    inFlightPathsRef.current = new Set();
    inFlightRef.current = 0;
    concurrencyRef.current = 2;
    successStreakRef.current = 0;

    let toUpload = items;
    const augmentedSkipped = [...skipped];
    if (items.length > 0) {
      setBatch({
        discovered: items.length + skipped.length,
        skipped: augmentedSkipped,
        queue: [],
        inFlight: [],
        completed: [],
        failed: [],
        startedAt: Date.now(),
        preflighting: true,
      });
      toast.info(`Checking ${items.length} files against library...`);
      const existing = await checkExistingOnServer(items);
      const filteredQueue = [];
      items.forEach((it) => {
        const key = `${it.file.name}|${it.file.size}`;
        if (existing.has(key)) {
          augmentedSkipped.push({
            path: it.relativePath,
            reason: 'Already in library (filename + size match)',
            size: it.file.size,
          });
        } else {
          filteredQueue.push(it);
        }
      });
      toUpload = filteredQueue;
      if (existing.size > 0) {
        toast.success(`${existing.size} file${existing.size !== 1 ? 's' : ''} already in library — skipping`);
      }
    }

    queueRef.current = [...toUpload];
    setBatch({
      discovered: items.length + skipped.length,
      skipped: augmentedSkipped,
      queue: [...toUpload],
      inFlight: [],
      completed: [],
      failed: [],
      startedAt: Date.now(),
      preflighting: false,
    });
    setTimeout(() => tryLaunchWorkers(), 0);
  }, [tryLaunchWorkers, checkExistingOnServer]);

  // Cleanup any stray timer on unmount
  useEffect(() => {
    return () => {
      if (tickHandleRef.current != null) {
        clearTimeout(tickHandleRef.current);
        tickHandleRef.current = null;
      }
    };
  }, []);

  const discoverFromFileList = (fileList, fromFolderPicker) => {
    const items = [];
    const skipped = [];
    Array.from(fileList).forEach((f) => {
      const rel = fromFolderPicker && f.webkitRelativePath ? f.webkitRelativePath : f.name;
      const parts = rel.split('/').filter(Boolean);
      const folderTags = parts.slice(0, -1);
      const result = evaluateFile(f);
      if (result.skip) {
        skipped.push({ path: rel, reason: result.reason, size: f.size });
      } else if (resumeFilter && !resumeFilter.keys.has(`${f.name}|${f.size}`)) {
        skipped.push({ path: rel, reason: 'Not in failure report — already uploaded successfully', size: f.size });
      } else {
        items.push({ file: f, folderTags, relativePath: rel });
      }
    });
    return { items, skipped };
  };

  const handleResumeReport = useCallback(async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.tsv') && !file.name.toLowerCase().endsWith('.txt')) {
      toast.error('Please select a .tsv failure report');
      return;
    }
    try {
      const text = await file.text();
      const lines = text.split('\n');
      const keys = new Set();
      let dataStarted = false;
      for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed) continue;
        if (trimmed.startsWith('PATH\t')) { dataStarted = true; continue; }
        if (!dataStarted) continue;
        const cols = line.split('\t');
        if (cols.length < 3) continue;
        const path = cols[0];
        const size = parseInt(cols[2], 10);
        if (!path || isNaN(size)) continue;
        const basename = path.split(/[/\\]/).pop();
        if (basename) keys.add(`${basename}|${size}`);
      }
      if (keys.size === 0) {
        toast.error('No valid entries found in the failure report');
        return;
      }
      setResumeFilter({ keys, count: keys.size, reportName: file.name });
      toast.success(`Loaded ${keys.size} failed files from report. Now select the original folder.`);
    } catch (err) {
      toast.error(`Failed to parse report: ${err.message}`);
    }
  }, [setResumeFilter]);

  const clearResumeFilter = () => {
    setResumeFilter(null);
    toast.info('Resume filter cleared');
  };

  const handleFilePickerChange = (e, fromFolder) => {
    const fileList = e.target.files;
    if (!fileList || fileList.length === 0) return;
    const { items, skipped } = discoverFromFileList(fileList, fromFolder);
    if (items.length === 0 && skipped.length === 0) return;
    if (items.length === 0) {
      toast.error('No supported files found');
    }
    startBatch(items, skipped);
    e.target.value = '';
  };

  const handleZip = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.zip')) {
      toast.error('Please upload a .zip file');
      return;
    }
    toast.info(`Uploading ZIP (${formatBytes(file.size)})...`);
    try {
      const res = await axios.post(`${API}/library/upload-zip`, file, {
        headers: { 'Content-Type': 'application/zip' },
        timeout: 0,
      });
      toast.success(`ZIP extracted: ${res.data.count} files queued for ingestion`);
      onComplete?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'ZIP upload failed');
    }
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    setDraggingOver(false);
    const dt = e.dataTransfer;
    if (dt.files.length === 1 && dt.files[0].name.toLowerCase().endsWith('.zip')) {
      handleZip(dt.files[0]);
      return;
    }
    const items = dt.items;
    if (items && items.length && items[0].webkitGetAsEntry) {
      toast.info('Scanning folder tree...');
      const allFiles = [];
      const allSkipped = [];
      for (const item of items) {
        const entry = item.webkitGetAsEntry();
        if (!entry) continue;
        try {
          // eslint-disable-next-line no-await-in-loop
          const walked = await walkEntry(entry, []);
          for (const w of walked) {
            const result = evaluateFile(w.file);
            if (result.skip) {
              allSkipped.push({ path: w.relativePath, reason: result.reason, size: w.file.size });
            } else if (resumeFilter && !resumeFilter.keys.has(`${w.file.name}|${w.file.size}`)) {
              allSkipped.push({ path: w.relativePath, reason: 'Not in failure report — already uploaded successfully', size: w.file.size });
            } else {
              allFiles.push(w);
            }
          }
        } catch (err) {
          console.error('walkEntry error', err);
        }
      }
      if (allFiles.length === 0 && allSkipped.length === 0) {
        toast.error('No files found');
        return;
      }
      startBatch(allFiles, allSkipped);
    } else {
      const { items: ok, skipped } = discoverFromFileList(dt.files, false);
      startBatch(ok, skipped);
    }
  };

  const downloadReport = (entries, kind) => {
    if (!entries.length) return;
    const labelMap = { skipped: 'Skip', failed: 'Failure' };
    const fileMap = { skipped: 'skipped', failed: 'failed' };
    const lines = [
      `AKS Tax & Legal — Library Upload ${labelMap[kind]} Report`,
      `Generated: ${new Date().toISOString()}`,
      `Total ${kind}: ${entries.length}`,
      '',
      'PATH\tREASON\tSIZE_BYTES',
    ];
    entries.forEach((s) => {
      lines.push(`${s.path}\t${s.reason}\t${s.size}`);
    });
    const blob = new Blob([lines.join('\n')], { type: 'text/tab-separated-values' });
    const url = window.URL.createObjectURL(blob);
    const a = window.document.createElement('a');
    a.href = url;
    a.download = `library-upload-${fileMap[kind]}-${Date.now()}.tsv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const dismissBatch = () => {
    if (batch && (batch.queue.length > 0 || batch.inFlight.length > 0)) {
      if (!window.confirm('Pending uploads will be cancelled. Continue?')) return;
    }
    queueRef.current = [];
    setBatch(null);
  };

  const totalForBatch = batch ? batch.completed.length + batch.failed.length + batch.queue.length + batch.inFlight.length : 0;
  const doneForBatch = batch ? batch.completed.length + batch.failed.length : 0;
  const pctForBatch = totalForBatch ? Math.round((doneForBatch / totalForBatch) * 100) : 0;
  const elapsedSec = batch ? Math.round((Date.now() - batch.startedAt) / 1000) : 0;
  const etaSec = batch && doneForBatch > 0 ? Math.round((elapsedSec / doneForBatch) * (totalForBatch - doneForBatch)) : null;

  return (
    <div className="space-y-3">
      {resumeFilter && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 flex items-start gap-2.5" data-testid="resume-filter-banner">
          <ListChecks className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-amber-900">
              Resume mode active — only {resumeFilter.count.toLocaleString()} failed file{resumeFilter.count !== 1 ? 's' : ''} will upload.
            </p>
            <p className="text-xs text-amber-700 truncate">Report: {resumeFilter.reportName}</p>
            <p className="text-xs text-amber-700 mt-1">Now select the same source folder — the system will skip all already-uploaded files automatically.</p>
          </div>
          <button
            onClick={clearResumeFilter}
            className="text-xs text-amber-700 hover:text-amber-900 underline flex-shrink-0"
            data-testid="resume-filter-clear"
          >
            Clear
          </button>
        </div>
      )}

      <div
        onDragOver={(e) => { e.preventDefault(); setDraggingOver(true); }}
        onDragLeave={() => setDraggingOver(false)}
        onDrop={handleDrop}
        className={`rounded-xl border-2 border-dashed p-6 text-center transition-all ${
          draggingOver
            ? 'border-[#1E3A8A] bg-[#1E3A8A]/5'
            : 'border-slate-300 bg-slate-50/50 hover:border-slate-400'
        }`}
        data-testid="library-dropzone"
      >
        <div className="w-12 h-12 mx-auto mb-3 bg-gradient-to-br from-[#1E3A8A] to-[#3B82F6] rounded-xl flex items-center justify-center shadow-md">
          <Upload className="w-6 h-6 text-white" />
        </div>
        <p className="font-semibold text-slate-800 mb-1">Drag & drop files or an entire folder</p>
        <p className="text-xs text-slate-500 mb-3">PDF, Word, Excel, TXT, HTML, MD — subfolders auto-tagged</p>
        <div className="flex items-center justify-center gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} data-testid="upload-files-btn">
            <FileText className="w-4 h-4 mr-1.5" />Select Files
          </Button>
          <Button variant="outline" size="sm" onClick={() => folderInputRef.current?.click()} data-testid="upload-folder-btn">
            <Folder className="w-4 h-4 mr-1.5" />Select Folder
          </Button>
          <Button variant="outline" size="sm" onClick={() => zipInputRef.current?.click()} data-testid="upload-zip-btn">
            <Archive className="w-4 h-4 mr-1.5" />Upload ZIP
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => reportInputRef.current?.click()}
            className={resumeFilter ? 'border-amber-300 bg-amber-50' : ''}
            data-testid="upload-resume-btn"
          >
            <Repeat className="w-4 h-4 mr-1.5" />Resume from Report
          </Button>
        </div>
        <input ref={fileInputRef} type="file" multiple accept={SUPPORTED_MIMES} className="hidden"
               onChange={(e) => handleFilePickerChange(e, false)} data-testid="library-file-input" />
        <input ref={folderInputRef} type="file" multiple webkitdirectory="" directory="" className="hidden"
               onChange={(e) => handleFilePickerChange(e, true)} data-testid="library-folder-input" />
        <input ref={reportInputRef} type="file" accept=".tsv,.txt" className="hidden"
               onChange={(e) => { handleResumeReport(e.target.files?.[0]); e.target.value = ''; }}
               data-testid="library-resume-input" />
        <input ref={zipInputRef} type="file" accept=".zip" className="hidden"
               onChange={(e) => handleZip(e.target.files?.[0])} data-testid="library-zip-input" />
      </div>

      {batch && (
        <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="upload-batch-panel">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <ListChecks className="w-4 h-4 text-[#1E3A8A]" />
              <span className="text-sm font-semibold text-slate-800">
                {batch.preflighting
                  ? 'Checking which files are already uploaded…'
                  : (batch.queue.length + batch.inFlight.length > 0 ? 'Uploading…' : 'Upload complete')}
              </span>
              <span className="text-xs text-slate-500">
                · {doneForBatch}/{totalForBatch} files
                {batch.queue.length + batch.inFlight.length > 0 && ` · ETA ${formatTime(etaSec)}`}
              </span>
            </div>
            <button onClick={dismissBatch} className="p-1 hover:bg-slate-100 rounded" title="Dismiss" data-testid="batch-dismiss-btn">
              <X className="w-4 h-4 text-slate-400" />
            </button>
          </div>

          <Progress value={pctForBatch} className="h-2 mb-3" />

          <div className="flex items-center gap-3 text-xs text-slate-500 mb-2 flex-wrap">
            <span className="flex items-center gap-1 text-emerald-600"><CheckCircle2 className="w-3 h-3" />{batch.completed.length} uploaded</span>
            {batch.inFlight.length > 0 && (
              <span className="flex items-center gap-1 text-blue-600"><Loader2 className="w-3 h-3 animate-spin" />{batch.inFlight.length} in flight</span>
            )}
            <span className="flex items-center gap-1">{batch.queue.length} queued</span>
            {batch.failed.length > 0 && (
              <span className="flex items-center gap-1 text-red-600"><AlertCircle className="w-3 h-3" />{batch.failed.length} failed</span>
            )}
            {batch.skipped.length > 0 && (
              <span className="flex items-center gap-1 text-amber-600"><FileX className="w-3 h-3" />{batch.skipped.length} skipped</span>
            )}
            <span className="text-slate-400">· max 2 parallel · auto-retry on network errors</span>
          </div>

          {(batch.queue.length + batch.inFlight.length > 0) && (
            <p className="text-[11px] bg-amber-50 border border-amber-200 text-amber-700 rounded px-2 py-1.5 mb-2">
              ⏱ Keep this tab open until the upload finishes. If a file fails, it will retry up to 4 times with backoff before being marked failed.
            </p>
          )}

          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => setShowDetails(v => !v)}
              className="text-xs font-medium text-slate-600 hover:text-[#1E3A8A] inline-flex items-center gap-1"
              data-testid="batch-toggle-details"
            >
              {showDetails ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
              {showDetails ? 'Hide' : 'Show'} details
            </button>
            {batch.skipped.length > 0 && (
              <Button size="sm" variant="outline" onClick={() => downloadReport(batch.skipped, 'skipped')} className="h-7 px-2 text-xs" data-testid="batch-skip-report-btn">
                <Download className="w-3 h-3 mr-1" />Skip report ({batch.skipped.length})
              </Button>
            )}
            {batch.failed.length > 0 && (
              <Button size="sm" variant="outline" onClick={() => downloadReport(batch.failed, 'failed')} className="h-7 px-2 text-xs" data-testid="batch-failed-report-btn">
                <Download className="w-3 h-3 mr-1" />Failure report ({batch.failed.length})
              </Button>
            )}
          </div>

          {showDetails && (
            <div className="mt-3 space-y-2 max-h-64 overflow-y-auto">
              {batch.inFlight.slice(0, 10).map((p) => (
                <div key={`if-${p}`} className="flex items-center gap-2 text-xs">
                  <Loader2 className="w-3 h-3 animate-spin text-blue-500 flex-shrink-0" />
                  <span className="text-slate-600 truncate flex-1" title={p}>{p}</span>
                </div>
              ))}
              {batch.failed.slice(0, 20).map((f) => (
                <div key={`f-${f.path}`} className="flex items-start gap-2 text-xs">
                  <AlertCircle className="w-3 h-3 text-red-500 flex-shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <span className="text-slate-700 block truncate" title={f.path}>{f.path}</span>
                    <span className="text-red-600 text-[11px]">{f.reason}</span>
                  </div>
                </div>
              ))}
              {batch.skipped.slice(0, 20).map((s) => (
                <div key={`s-${s.path}`} className="flex items-start gap-2 text-xs">
                  <FileX className="w-3 h-3 text-amber-500 flex-shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <span className="text-slate-700 block truncate" title={s.path}>{s.path}</span>
                    <span className="text-amber-600 text-[11px]">{s.reason}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Uploader;
